// Shared ERA date-group status metadata + formatting helpers.
//
// Single source of truth for how a dateGroup `status` renders as a badge and
// which Matched/Unmatched class it belongs to. Consumed by EraPatientGroups
// (the ERA detail view) so the labels stay in lock-step with the ERAList
// summary badges and the backend classifier in eraReportingRoutes.js.

export const STATUS_META = {
  allocated: { label: "Matched", bg: "success", icon: "fa-circle-check" },
  unallocated: { label: "Unmatched", bg: "danger", icon: "fa-circle-exclamation" },
  // Already settled = matched to a claim a prior ERA already finalised; the
  // cash landed, so it reads as a Matched sub-state.
  skipped_finalised: {
    label: "Matched - Already Settled",
    bg: "secondary",
    icon: "fa-circle-info",
  },
  // Matched claim/invoice was soft-deleted before the ERA arrived — nothing to
  // apply against, so it reads as an Unmatched sub-state for the practice.
  skipped_deleted: {
    label: "Unmatched - Deleted Invoice / Claim",
    bg: "danger",
    icon: "fa-trash",
  },
  duplicate_in_bundle: {
    label: "Unmatched - Duplicate",
    bg: "warning",
    icon: "fa-clone",
  },
  // Reversals/clawbacks surface as Unmatched (the practice sorts them) but keep
  // the "Reversal" reason so the audit trail shows WHY it's unmatched.
  reversal: {
    label: "Unmatched - Contains Reversal",
    bg: "danger",
    icon: "fa-rotate-left",
  },
  // All dismissals are a manual finance action (no auto-dismiss exists), so
  // the badge says so.
  dismissed: { label: "Dismissed - Manually", bg: "dark", icon: "fa-eye-slash" },
};

export function statusMeta(status) {
  return (
    STATUS_META[status] || { label: status || "—", bg: "light", icon: "fa-question" }
  );
}

// Which top-level bucket a dateGroup status belongs to. Mirrors the backend
// MATCHED_STATUSES / UNMATCHED_STATUSES split and the ERAList classifier.
export function classOfStatus(status) {
  return status === "allocated" || status === "skipped_finalised"
    ? "matched"
    : "unmatched";
}

// dateGroup.date is normalised to "YYYYMMDD" server-side. `new Date("20260506")`
// returns Invalid Date (the constructor only parses ISO strings with
// separators), so insert the hyphens before formatting. Pass-through for
// already-ISO input keeps the helper safe.
export function formatGroupDate(raw) {
  if (!raw) return "—";
  const s = String(raw);
  const iso = /^\d{8}$/.test(s)
    ? `${s.slice(0, 4)}-${s.slice(4, 6)}-${s.slice(6, 8)}`
    : s;
  const d = new Date(iso);
  return isNaN(d.getTime()) ? "—" : d.toLocaleDateString("en-GB");
}

export function fmtZAR(x) {
  if (x === null || x === undefined || x === "") return "";
  return new Intl.NumberFormat("en-ZA", {
    style: "currency",
    currency: "ZAR",
    minimumFractionDigits: 2,
  }).format(Number(x) || 0);
}
